import { mdiArrowRight, mdiCommentQuestionOutline } from '@mdi/js';
import Icon from '@mdi/react';
import { useGetApiV2MemberEmployments } from '@shared/integrations/taf-api/v2/features/member/member';
import { RetirementForm } from '@my-pages/features/employment/Forms/RetirementForm.tsx';
import { UnemploymentForm } from '@my-pages/features/employment/Forms/UnemploymentForm.tsx';
import { StudyForm } from '@my-pages/features/employment/Forms/StudyForm.tsx';
import { ParentalLeaveForm } from '@my-pages/features/employment/Forms/ParentalLeaveForm.tsx';
import { IncomeForm } from '@my-pages/features/employment/Forms/IncomeForm.tsx';
import { NewEmploymentFormHandler } from '@my-pages/features/employment/Forms/NewEmploymentForm.tsx';
import { isEmployed, isOnParentalLeave, isStudent, isUnemployed } from '@my-pages/features/employment/services/EmploymentService.ts';
import { IEmploymentInfo } from '@shared/integrations/taf-api/v2/models';
import { EmploymentUpdateStatus } from '@my-pages/features/employment/types';

interface IToggleEmploymentFormsCardProps {
    setSelectedForm: any;
    setDisplayMessage: (value: EmploymentUpdateStatus | null) => void;
}

const ToggleEmploymentFormsCard = ({ setSelectedForm, setDisplayMessage }: IToggleEmploymentFormsCardProps) => {
    const { data } = useGetApiV2MemberEmployments();

    const employment = data?.current ? data.current[0] : {};

    const ChangeIncomeDescription = IncomeDescriptionOptions(employment);
    const ChangeEmploymentDescription = NewEmploymentDescriptionOptions(employment);
    const ChangeEmploymentOptions = [
        {
            description: 'Jag har blivit arbetslös.',
            inputform: (
                <UnemploymentForm
                    employment={employment}
                    closeForm={() => setSelectedForm(null)}
                    setDisplayMessage={setDisplayMessage}
                />
            ),
        },
        {
            description: 'Jag ska gå i pension.',
            inputform: (
                <RetirementForm
                    employment={employment}
                    closeForm={() => setSelectedForm(null)}
                    setDisplayMessage={setDisplayMessage}
                />
            ),
        },
        {
            description: 'Jag har börjat studera.',
            inputform: (
                <StudyForm
                    employment={employment}
                    closeForm={() => setSelectedForm(null)}
                    setDisplayMessage={setDisplayMessage}
                />
            ),
        },
        {
            description: 'Jag har blivit föräldraledig.',
            inputform: (
                <ParentalLeaveForm
                    employment={employment}
                    closeForm={() => setSelectedForm(null)}
                    setDisplayMessage={setDisplayMessage}
                />
            ),
        },
    ];

    return (
        <div className='card card-body mb-4'>
            <div className='row'>
                <div className='col-12'>
                    <h3>Har något ändrats sedan du loggade in sist?</h3>
                    <button
                        role='button'
                        className='click-item'
                        onClick={() =>{
                            setSelectedForm(
                                <IncomeForm
                                    employment={employment}
                                    closeForm={() => setSelectedForm(null)}
                                    setDisplayMessage={setDisplayMessage}

                                />,
                            )
                            setDisplayMessage(null);
                        }}
                    >
                        <div className='click-item-body mr-3'>{ChangeIncomeDescription}</div>
                        <Icon
                            path={mdiArrowRight}
                            className='mdi mdi-36 text-primary ml-auto'
                        />
                    </button>
                    <button
                        role='button'
                        className='click-item'
                        onClick={() =>{
                            setSelectedForm(
                                <NewEmploymentFormHandler
                                    employment={employment}
                                    closeForm={() => setSelectedForm(null)}
                                    setDisplayMessage={setDisplayMessage}
                                />,
                            )
                            setDisplayMessage(null);
                        }}
                    >
                        <div className='click-item-body mr-3'>{ChangeEmploymentDescription}</div>
                        <Icon
                            path={mdiArrowRight}
                            className='mdi mdi-36 text-primary ml-auto'
                        />
                    </button>
                    {ChangeEmploymentOptions.map(({ description, inputform }, i) => (
                        <button
                            key={i}
                            role='button'
                            className='click-item'
                            onClick={() => {
                                setSelectedForm(inputform)
                                setDisplayMessage(null);
                            }}
                        >
                            <div className='click-item-body mr-3'>{description}</div>
                            <Icon
                                path={mdiArrowRight}
                                className='mdi mdi-36 text-primary ml-auto'
                            />
                        </button>
                    ))}
                    <div
                        className='alert alert-info mt-5 mb-0'
                        role='alert'
                    >
                        <h5 className='alert-heading'>
                            <Icon
                                path={mdiCommentQuestionOutline}
                                className='mdi mr-1'
                            />
                            Finns det inget alternativ som passar just din situation?
                        </h5>
                        <p>
                            Om inget av alternativen ovan passar dig men du ändå behöver hjälp, ring oss på{' '}
                            <a
                                href='tel:010-480 30 00'
                                className='text-nowrap'
                            >
                                010-480 30 00
                            </a>
                            .
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

const IncomeDescriptionOptions = (employment: IEmploymentInfo) => {
    if (isEmployed(employment)) {
        return 'Jag har samma anställning, men min inkomst har ändrats';
    } else if (isOnParentalLeave(employment)) {
        return 'Jag är fortfarande föräldraledig, men min ersättning har ändrats';
    } else if (isStudent(employment)) {
        return 'Jag är fortfarande student, men min inkomst har ändrats';
    } else if (isUnemployed(employment)) {
        return 'Jag är fortfarande arbetslös men min ersättning har ändrats';
    } else {
        return 'Min inkomst/ersättning har ändrats';
    }
};

const NewEmploymentDescriptionOptions = (employment: IEmploymentInfo) => {
    if (isEmployed(employment)) {
        return 'Jag arbetar fortfarande, men min inkomst och anställning har ändrats';
    }
    return 'Jag har återgått till arbete';
};

export default ToggleEmploymentFormsCard;
